// These MUI icons are temporary while the settings sidebar is being redesigned
import {
  AutoAwesomeOutlined as AgentLibraryIcon,
  Settings as AdminSettingsIcon,
  Receipt as BudgetApprovalsIcon,
  Flag as FeatureFlagsIcon,
  SyncAlt as EnvironmentSyncIcon,
  Translate,
} from '@mui/icons-material'
import { Box, Stack } from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAgentLibraryLdFlags } from '@omnea-app/fe-agent-library/launch-darkly'
import { LIBRARY_PATH } from '@omnea-app/fe-agent-library/libraryNav'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useVisibleCatalogueSettings } from '@omnea-app/fe-settings/catalogues/useVisibleCatalogueSettings'
import { useSettingsLdFlags } from '@omnea-app/fe-settings/launch-darkly'
import { FeatureFlagsEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { filterSectionsBySearch } from '../filterSectionsBySearch'
import { useIntegrationsItems } from '../hooks/useIntegrationsItems'
import { SidebarList, SidebarListItemButton, SidebarListItemText, SidebarSectionTitle } from '../StyledSidebarComponents'
import { getSettingsIconByName } from './SettingsIcon'
import { SettingsSearchbar } from './SettingsSearchbar'
import type { AccessAnswer } from '@omnea-app/fe-authz/resolveAccess'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'

// TODO(PLT-1723): derive `href` from the route key so the two cannot disagree — a key naming a route
// other than the item's destination shows the link to callers the gate will turn away. Any item whose
// href carries a parameter needs that mechanism first, which is why the two are still separate fields.
export type SettingsMenuItem = {
  label: string
  icon: React.ReactElement
  /** Absent only for a destination the registry cannot name — an external URL. */
  routeKey?: RouteKey
  showsOmneanOnlyBadge?: boolean
  href: string
  newTab?: boolean
}

export type SettingsSection = {
  title: string
  items: SettingsMenuItem[]
}

const isMenuItemVisible = (item: SettingsMenuItem, access: ReadonlyMap<RouteKey, AccessAnswer>): boolean =>
  item.routeKey == null || access.get(item.routeKey) === 'allowed'

const PREVENT_BROWSER_URL_TOOLTIP_OVERLAP_PADDING = 14

export const SettingsSidebarContent = () => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState('')
  const featureFlags = useFeatureFlags()
  const ldFeatureFlags = useSettingsLdFlags()
  const { RELEASE_AGENT_MARKETPLACE_V1 } = useAgentLibraryLdFlags()
  const routeAccess = useRouteAccess()
  const integrationsItems = useIntegrationsItems(routeAccess.get('settingsIntegrations') === 'allowed')
  const hasVisibleCatalogueSettings = useVisibleCatalogueSettings().length > 0

  const personalSettingSection: SettingsSection = {
    title: t('sidebar.account-title'),
    items: [
      {
        label: t('sidebar.notifications'),
        icon: getSettingsIconByName('notifications'),
        href: '/settings/notifications',
        routeKey: 'settingsNotifications',
      },
      {
        label: t('sidebar.preferences'),
        icon: getSettingsIconByName('preferences'),
        href: '/settings/preferences',
        routeKey: 'settingsPreferences',
      },
    ],
  }

  const organisationSection: SettingsSection = {
    title: t('sidebar.organisation-title'),
    items: [
      {
        label: t('sidebar.default-notifications'),
        icon: getSettingsIconByName('notifications'),
        href: '/settings/default-notifications',
        routeKey: 'settingsDefaultNotifications',
      },
      {
        label: t('sidebar.ai'),
        icon: getSettingsIconByName('ai'),
        href: '/settings/ai',
        routeKey: 'settingsAi',
      },
      {
        label: t('sidebar.security'),
        icon: getSettingsIconByName('security'),
        href: '/settings/security',
        routeKey: 'settingsSecurity',
      },
      {
        label: t('sidebar.core-data'),
        icon: getSettingsIconByName('core-data'),
        href: '/settings/core-data',
        routeKey: 'settingsCoreData',
      },
      {
        label: t('sidebar.users-and-groups'),
        icon: getSettingsIconByName('users'),
        href: '/settings/users-and-groups',
        routeKey: 'settingsUsersAndGroups',
      },
      {
        label: t('sidebar.permissions'),
        icon: getSettingsIconByName('security'),
        href: '/settings/permissions',
        routeKey: 'settingsPermissions',
      },
      {
        label: t('sidebar.user-properties'),
        icon: getSettingsIconByName('user-properties'),
        href: '/settings/user-properties',
        routeKey: 'settingsUserProperties',
      },
      {
        label: t('sidebar.api'),
        icon: getSettingsIconByName('api'),
        href: '/settings/api',
        routeKey: 'settingsApi',
      },
      {
        label: t('sidebar.import'),
        icon: getSettingsIconByName('import'),
        href: '/settings/import',
        routeKey: 'settingsImport',
      },
      {
        label: t('sidebar.audit-log'),
        icon: getSettingsIconByName('audit-log'),
        href: '/settings/audit-log',
        routeKey: 'settingsAuditLog',
      },
      {
        label: t('sidebar.external-archive'),
        icon: getSettingsIconByName('audit-log'),
        href: '/settings/external-archive',
        routeKey: 'settingsExternalArchive',
        showsOmneanOnlyBadge: true,
      },
    ],
  }

  const featuresSection: SettingsSection = {
    title: t('sidebar.features-title'),
    items: [
      ...(RELEASE_AGENT_MARKETPLACE_V1
        ? [
            {
              label: t('sidebar.agent-library'),
              icon: <AgentLibraryIcon sx={{ width: 18 }} />,
              href: LIBRARY_PATH,
              routeKey: 'settingsAgentLibrary' as const,
            },
          ]
        : []),
      {
        label: t('sidebar.workflows'),
        icon: getSettingsIconByName('workflows'),
        href: '/settings/workflows',
        routeKey: 'settingsWorkflows',
      },
      {
        label: t('sidebar.budget-approvals'),
        icon: <BudgetApprovalsIcon sx={{ width: 18 }} />,
        href: '/settings/dynamic-assignees',
        routeKey: 'settingsDynamicAssignees',
      },
      {
        label: t('sidebar.forms'),
        icon: getSettingsIconByName('questions'),
        href: '/settings/forms',
        routeKey: 'settingsForms',
      },
      {
        label: t('sidebar.sourcing'),
        icon: getSettingsIconByName('sourcing'),
        href: '/settings/sourcing',
        routeKey: 'settingsSourcing',
      },
      {
        label: t('sidebar.auctions'),
        icon: getSettingsIconByName('auctions'),
        href: '/settings/auctions',
        routeKey: 'settingsAuctions',
      },
      {
        label: t('sidebar.risk-management'),
        icon: getSettingsIconByName('risk-management'),
        href: '/settings/risks',
        routeKey: 'settingsRisks',
      },
      // Which catalogue kinds an organisation has is data, not a route rule, so it stays an item condition.
      ...(hasVisibleCatalogueSettings
        ? [
            {
              label: t('sidebar.catalogues'),
              icon: getSettingsIconByName('catalogues'),
              href: '/settings/catalogues',
              routeKey: 'settingsCatalogues' as const,
            },
          ]
        : []),
      {
        label: t('sidebar.product-supplier-categories'),
        icon: getSettingsIconByName('product-supplier-categories'),
        href: '/settings/categories',
        routeKey: 'settingsCategories',
      },
      {
        label: ldFeatureFlags.SHOW_DOCUMENT_REVIEW ? t('sidebar.document-review-playbooks') : t('sidebar.document-storage'),
        icon: getSettingsIconByName('document-storage'),
        href: '/settings/document-storage',
        routeKey: 'settingsDocumentStorage',
      },
      {
        label: t('sidebar.policies'),
        icon: getSettingsIconByName('policies'),
        href: '/settings/policies',
        routeKey: 'settingsPolicies',
      },
      {
        label: t('sidebar.environment-sync'),
        icon: <EnvironmentSyncIcon sx={{ width: 18, height: 18 }} />,
        href: '/settings/environment-sync',
        routeKey: 'settingsEnvironmentSync',
      },
      {
        label: t('sidebar.feature-flags', 'Feature flags'),
        icon: <FeatureFlagsIcon sx={{ width: 18, height: 18 }} />,
        href: '/settings/__feature-flags',
        routeKey: 'settingsInternalFeatureFlags',
        showsOmneanOnlyBadge: true,
      },
      {
        label: t('sidebar.localisation'),
        icon: <Translate sx={{ width: 18, height: 18 }} />,
        href: '/settings/localisation',
        routeKey: 'settingsLocalisation',
      },
      {
        label: t('sidebar.admin-settings'),
        icon: <AdminSettingsIcon sx={{ width: 18, height: 18 }} />,
        href: '/settings/admin',
        routeKey: 'settingsAdmin',
      },
      // The migration tool exists to migrate organisations onto agreements, so the flag hides the item
      // for those already on them rather than gating the route.
      ...(featureFlags[FeatureFlagsEnum.CREATE_AGREEMENT] === true
        ? [
            {
              label: t('sidebar.agreements-migration'),
              icon: <AdminSettingsIcon sx={{ width: 18, height: 18 }} />,
              href: '/settings/agreements-migration',
              routeKey: 'settingsAgreementsMigration' as const,
              showsOmneanOnlyBadge: true,
            },
          ]
        : []),
    ],
  }

  const accountsPayableSection: SettingsSection = {
    title: t('sidebar.accounts-payable-title'),
    items: [
      {
        label: t('sidebar.goods-receipting'),
        icon: getSettingsIconByName('goods-receipting'),
        href: '/settings/goods-receipting',
        routeKey: 'settingsGoodsReceipting',
      },
      {
        label: t('sidebar.invoice-processing'),
        icon: getSettingsIconByName('invoice-processing'),
        href: '/settings/invoice-processing',
        routeKey: 'settingsInvoiceProcessing',
      },
    ],
  }

  const integrationsSection: SettingsSection = {
    title: t('sidebar.integrations'),
    items: integrationsItems,
  }

  const sections: SettingsSection[] = [
    personalSettingSection,
    organisationSection,
    featuresSection,
    accountsPayableSection,
    integrationsSection,
  ]

  const permissionFilteredSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => isMenuItemVisible(item, routeAccess)),
    }))
    .filter((section) => section.items.length > 0)

  const filteredSections = filterSectionsBySearch(permissionFilteredSections, searchQuery)

  return (
    <Stack spacing={3} sx={{ height: '100%', overflow: 'hidden' }}>
      {/* Menu sections */}
      <Stack
        spacing={2}
        sx={{
          pb: PREVENT_BROWSER_URL_TOOLTIP_OVERLAP_PADDING,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <SettingsSearchbar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        {filteredSections.map((section: SettingsSection) => (
          <Box key={section.title}>
            <SidebarSectionTitle>{section.title}</SidebarSectionTitle>
            <SidebarList>
              {section.items.map((item: SettingsMenuItem) => {
                // Compared without the query string: a page that keeps state there is still that page
                const isSelected = router.asPath?.split('?')[0] === item.href
                const newTabProps = item.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}
                return (
                  <Link key={item.href} href={item.href} passHref {...newTabProps}>
                    <SidebarListItemButton selected={isSelected}>
                      <Box sx={{ minWidth: 24, color: 'fg.muted', mr: 2, mt: 1 }}>{item.icon}</Box>
                      <SidebarListItemText
                        primary={item.label}
                        secondary={item.showsOmneanOnlyBadge === true ? t('sidebar.omnean-only') : undefined}
                      />
                    </SidebarListItemButton>
                  </Link>
                )
              })}
            </SidebarList>
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}
